import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import api from '../../../services/api';
import { formatDate } from '../../../utils/Utils';
import { dateFilterColumn } from '../../../utils/dateFilterColumn';

// Analitik Plan 4 (spec §6.5) — performans aksiyonları. PerformanceActionsController rotaları (BE ile birebir):
//   GET   v1/performance-actions?status[in]=&actionType[eq]=&dueDate[between]=&pageNumber=&pageSize=
//         (İK hepsini, diğerleri yalnız sorumlusu oldukları aksiyonları görür)
//   POST  v1/performance-actions        {title, description, ownerEmployeeId, teamLabel, dueDate, actionType, periodId?, sourceInsightId?, linkedScopeType?, linkedScopeId?}  (İK)
//   PUT   v1/performance-actions/{id}   (İK)
//   PATCH v1/performance-actions/{id}/status {status}  (sorumlu: planned → in_progress → done; İK: iptal dahil)

// P7a — AI03 beklenti görüşmesi, AI06 gelişim planı taslağı.
export const ACTION_TYPES = ['reminder', 'goal_review', 'calibration_candidate', 'potential_assessment', 'succession_review', 'expectation_meeting', 'development_plan', 'custom'];
export const ACTION_STATUSES = ['planned', 'in_progress', 'done', 'cancelled'];

export const ACTION_STATUS_COLORS = {
  planned: { color: '#0291EF', bg: '#EFF6FF' },
  in_progress: { color: '#D97706', bg: '#FFFBEB' },
  done: { color: '#16A34A', bg: '#DCFCE7' },
  cancelled: { color: '#6B7280', bg: '#F3F4F6' },
};

export const StatusBadge = styled.span`
  display: inline-block;
  padding: 4px 10px;
  border-radius: 12px;
  font-size: ${({ theme }) => theme.metrics.font.size.xs};
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
`;

const OverdueText = styled.span`
  color: #DC2626;
  font-weight: ${({ theme }) => theme.metrics.font.weight.semiBold};
`;

const isOpen = (status) => status === 'planned' || status === 'in_progress';

// Son tarih "date" kolonudur (UTC gece yarısı) — gecikme yerel gün sonuna göre değil, takvim gününe göre.
export const isOverdue = (row) => {
  if (!row?.dueDate || !isOpen(row.status)) return false;
  const due = String(row.dueDate).slice(0, 10);
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return due < todayStr;
};

// Faz 2 Plan 2 (spec §5.7) — "Hatırlatmayı Gönder" yalnız açık, dönemli `reminder` aksiyonunda (sunucu da sorumlu/İK denetler).
export const canSendReminder = (row) => !!row && row.actionType === 'reminder' && isOpen(row.status) && !!row.periodId;

// Sorumlu: yalnız ileri. İK: iptal dahil her geçiş (iptal kapanmıştır).
export const nextStatuses = (row, canManage) => {
  if (!row || row.status === 'cancelled') return [];
  if (canManage) return ACTION_STATUSES.filter((s) => s !== row.status);
  if (row.status === 'planned') return ['in_progress', 'done'];
  if (row.status === 'in_progress') return ['done'];
  return [];
};

const selectFilter = (t, labelKey, options, labelOf) => ({
  display: (fl, onChange, i, col) => (
    <FormControl variant="standard" fullWidth>
      <InputLabel>{t(labelKey)}</InputLabel>
      <Select
        value={fl[i][0] || ''}
        onChange={(e) => {
          fl[i] = e.target.value ? [e.target.value] : [];
          onChange(fl[i], i, col);
        }}
      >
        <MenuItem value=""><em>-</em></MenuItem>
        {options.map((o) => <MenuItem key={o} value={o}>{labelOf(o)}</MenuItem>)}
      </Select>
    </FormControl>
  ),
});

const usePerformanceActionsApi = (queryParams) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [fetching, setFetching] = useState(true);
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({ totalRowCount: 0, pageSize: 10, pageIndex: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const refresh = useCallback(() => setRefreshTrigger((p) => p + 1), []);

  useEffect(() => {
    const params = queryParams && Object.keys(queryParams).length > 0 ? queryParams : { pageNumber: 1, pageSize: 10 };
    setFetching(true);
    api.get(`/v1/performance-actions?${new URLSearchParams(params)}`)
      .then((res) => {
        // api.js GET'te reject etmez — sinyal res.data.status.
        if (!res?.data?.status) { setData([]); return; }
        const result = res.data.data;
        const items = Array.isArray(result) ? result : (result?.data || []);
        setData(items);
        setPagination({
          totalRowCount: result?.totalRowCount || items.length,
          pageIndex: result?.pageIndex ? result.pageIndex - 1 : 0,
          pageSize: result?.pageSize || items.length || 10,
        });
      })
      .finally(() => setFetching(false));
  }, [queryParams, refreshTrigger]);

  const create = useCallback(async (payload) => {
    try {
      const res = await api.post('/v1/performance-actions', payload);
      refresh();
      return res?.data?.data || true;
    } catch {
      return false;
    }
  }, [refresh]);

  const update = useCallback(async (id, payload) => {
    try {
      await api.put(`/v1/performance-actions/${id}`, payload);
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const changeStatus = useCallback(async (id, status) => {
    try {
      await api.patch(`/v1/performance-actions/${id}/status`, { status });
      refresh();
      return true;
    } catch {
      return false;
    }
  }, [refresh]);

  const columns = useMemo(() => {
    // Gizli sayaç sütununun rowData indeksi sütun dizisinden okunur (sabit indeks sütun eklenince kayardı).
    let countIndex = -1;
    const cols = [
      { name: 'id', label: 'ID', options: { filter: false, sort: false, display: 'excluded' } },
      { name: 'title', label: t('performance.action.title'), options: { filter: false, sort: false } },
      {
        name: 'ownerName',
        label: t('performance.action.owner'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'actionType',
        label: t('performance.action.actionType'),
        options: {
          filter: true,
          sort: false,
          filterType: 'custom',
          customBodyRender: (v) => t(`performance.action.type.${v}`, { defaultValue: v }),
          filterOptions: { defaultOp: 'eq', ...selectFilter(t, 'performance.action.actionType', ACTION_TYPES, (o) => t(`performance.action.type.${o}`)) },
        },
      },
      {
        ...dateFilterColumn('dueDate', t('performance.action.dueDate'), lang, { sort: false }),
        options: {
          ...dateFilterColumn('dueDate', t('performance.action.dueDate'), lang, { sort: false }).options,
          customBodyRender: (v, tableMeta) => {
            if (!v) return '-';
            const text = formatDate(v, lang);
            const row = { dueDate: v, status: tableMeta?.rowData?.[6] };
            return isOverdue(row) ? <OverdueText title={t('performance.action.overdue')}>{text}</OverdueText> : text;
          },
        },
      },
      {
        name: 'teamLabel',
        label: t('performance.action.teamLabel'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'status',
        label: t('performance.action.status'),
        options: {
          filter: true,
          sort: false,
          filterType: 'custom',
          customBodyRender: (v) => {
            const cfg = ACTION_STATUS_COLORS[v] || ACTION_STATUS_COLORS.planned;
            return <StatusBadge color={cfg.color} bg={cfg.bg}>{t(`performance.action.statusValue.${v}`, { defaultValue: v })}</StatusBadge>;
          },
          filterOptions: { defaultOp: 'in', ...selectFilter(t, 'performance.action.status', ACTION_STATUSES, (o) => t(`performance.action.statusValue.${o}`)) },
        },
      },
      {
        name: 'periodName',
        label: t('performance.action.period'),
        options: { filter: false, sort: false, customBodyRender: (v) => v || '-' },
      },
      {
        name: 'sourceRuleCode',
        label: t('performance.action.source'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v) => (v ? t(`performance.insight.${v}.title`, { defaultValue: v }) : '-'),
        },
      },
      { name: 'lastReminderRecipientCount', label: 'lastReminderRecipientCount', options: { filter: false, sort: false, display: 'excluded' } },
      {
        // Faz 2 Plan 2 (spec §5.7) — "Son gönderim: tarih, N kişi" (aksiyondan yapılan en son toplu hatırlatma).
        name: 'lastReminderAt',
        label: t('performance.action.lastReminder'),
        options: {
          filter: false,
          sort: false,
          customBodyRender: (v, tableMeta) => {
            if (!v) return '-';
            const count = tableMeta?.rowData?.[countIndex];
            return t('performance.action.lastReminderValue', { date: formatDate(v, lang), count: count ?? 0 });
          },
        },
      },
    ];
    countIndex = cols.findIndex((c) => c.name === 'lastReminderRecipientCount');
    return cols;
  }, [t, lang]);

  return { fetching, data, pagination, columns, create, update, changeStatus, refresh };
};

export default usePerformanceActionsApi;
